import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { 
  MessageChunkData, 
  MessageCompleteData, 
  MessageErrorData, 
  MessageStartData,
  CitationData,
  StatusData 
} from '@/services/socketService'

export interface StreamingMessage {
  id: string
  sessionId: string
  content: string
  isComplete: boolean
  isStreaming: boolean
  hasError: boolean
  error?: string
  chunks: string[]
  citations: CitationData['citation'][]
  status: 'searching' | 'generating' | 'processing' | 'complete' | 'error' | 'idle'
  statusMessage?: string
  timestamp: string
}

export interface ConnectionState {
  connected: boolean
  connecting: boolean
  reconnecting: boolean
  reconnectAttempts: number
  maxReconnectAttempts: number
  error?: string
  lastConnected?: string
  lastDisconnected?: string
}

export interface TypingState {
  [sessionId: string]: {
    [userId: string]: {
      isTyping: boolean
      lastUpdate: Date
    }
  }
}

interface SocketState {
  connection: ConnectionState
  streamingMessages: { [messageId: string]: StreamingMessage }
  activeStreams: string[]
  typing: TypingState
  joinedSessions: string[]
}

const initialState: SocketState = {
  connection: {
    connected: false,
    connecting: false,
    reconnecting: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 5
  },
  streamingMessages: {},
  activeStreams: [],
  typing: {},
  joinedSessions: []
}

const socketSlice = createSlice({
  name: 'socket',
  initialState,
  reducers: {
    // Connection state actions
    connectionStarted: (state) => {
      state.connection.connecting = true
      state.connection.error = undefined
    },

    connectionSuccess: (state) => {
      state.connection.connected = true
      state.connection.connecting = false
      state.connection.reconnecting = false
      state.connection.reconnectAttempts = 0
      state.connection.error = undefined
      state.connection.lastConnected = new Date().toISOString()
    },

    connectionError: (state, action: PayloadAction<{ error: string }>) => {
      state.connection.connected = false
      state.connection.connecting = false
      state.connection.error = action.payload.error
    },

    connectionLost: (state, action: PayloadAction<{ reason: string }>) => {
      state.connection.connected = false
      state.connection.lastDisconnected = new Date().toISOString()
      // Don't clear error - let user know what happened
    },

    connectionReconnecting: (state, action: PayloadAction<{ attempt: number }>) => {
      state.connection.reconnecting = true
      state.connection.reconnectAttempts = action.payload.attempt
    },

    connectionRestored: (state, action: PayloadAction<{ attempts: number }>) => {
      state.connection.connected = true
      state.connection.reconnecting = false
      state.connection.reconnectAttempts = 0
      state.connection.error = undefined
      state.connection.lastConnected = new Date().toISOString()
    },

    connectionFailed: (state) => {
      state.connection.connected = false
      state.connection.connecting = false
      state.connection.reconnecting = false
      state.connection.error = 'Failed to reconnect after maximum attempts'
    },

    connectionTerminated: (state) => {
      state.connection.connected = false
      state.connection.connecting = false
      state.connection.reconnecting = false
      state.connection.error = 'Connection terminated by server'
    },

    // Session management
    sessionJoined: (state, action: PayloadAction<{ sessionId: string }>) => {
      const sessionId = action.payload.sessionId
      if (!state.joinedSessions.includes(sessionId)) {
        state.joinedSessions.push(sessionId)
      }
    },

    // Transfer streaming messages from temp session to real session
    transferStreamingMessages: (state, action: PayloadAction<{ 
      fromSessionId: string
      toSessionId: string 
    }>) => {
      const { fromSessionId, toSessionId } = action.payload
      
      // Find and update all streaming messages from temp session to real session
      Object.keys(state.streamingMessages).forEach(messageId => {
        if (state.streamingMessages[messageId].sessionId === fromSessionId) {
          state.streamingMessages[messageId].sessionId = toSessionId
        }
      })
    },

    sessionLeft: (state, action: PayloadAction<{ sessionId: string }>) => {
      const sessionId = action.payload.sessionId
      state.joinedSessions = state.joinedSessions.filter(id => id !== sessionId)
      // Clear typing state for this session
      delete state.typing[sessionId]
    },

    // Streaming message actions
    messageStreamStarted: (state, action: PayloadAction<MessageStartData>) => {
      const { messageId, sessionId, timestamp } = action.payload
      state.streamingMessages[messageId] = {
        id: messageId,
        sessionId,
        content: '',
        isComplete: false,
        isStreaming: true,
        hasError: false,
        chunks: [],
        citations: [],
        status: 'processing',
        timestamp: timestamp instanceof Date ? timestamp.toISOString() : timestamp
      }
      if (!state.activeStreams.includes(messageId)) {
        state.activeStreams.push(messageId)
      }
    },

    messageChunkReceived: (state, action: PayloadAction<MessageChunkData>) => {
      const { messageId, chunk, index } = action.payload
      const message = state.streamingMessages[messageId]
      
      if (message) {
        // Ensure chunks are in correct order
        message.chunks[index] = chunk
        
        // Rebuild content from chunks (handles out-of-order chunks)
        message.content = message.chunks.filter(c => c !== undefined).join('')
        message.isStreaming = true
      }
    },

    messageCitationReceived: (state, action: PayloadAction<CitationData>) => {
      const { messageId, citation } = action.payload
      const message = state.streamingMessages[messageId]
      
      if (message) {
        // Check if this citation already exists to prevent duplicates
        const exists = message.citations.some(
          existing => existing.documentId === citation.documentId &&
                     existing.pageNumber === citation.pageNumber
        )
        
        if (!exists) {
          message.citations.push(citation)
          console.log(`📚 Added citation for ${messageId}: ${citation.documentTitle}`)
        } else {
          console.log(`🔄 Skipped duplicate citation for ${messageId}: ${citation.documentTitle}`)
        }
      }
    },

    messageStatusUpdated: (state, action: PayloadAction<StatusData>) => {
      const { messageId, status, message: statusMessage } = action.payload
      const message = state.streamingMessages[messageId]
      
      if (message) {
        message.status = status
        message.statusMessage = statusMessage
        
        // Keep streaming active for processing statuses, complete only when truly done
        if (status === 'searching' || status === 'generating' || status === 'processing') {
          message.isStreaming = true
          // Ensure it's in active streams for hasActiveStreams calculation
          if (!state.activeStreams.includes(messageId)) {
            state.activeStreams.push(messageId)
          }
        } else if (status === 'complete' || status === 'error') {
          message.isStreaming = false
          message.isComplete = status === 'complete'
          // Remove from active streams
          state.activeStreams = state.activeStreams.filter(id => id !== messageId)
        }
      }
    },

    messageStreamCompleted: (state, action: PayloadAction<MessageCompleteData>) => {
      const { messageId, fullContent, citations, timestamp } = action.payload
      const message = state.streamingMessages[messageId]
      
      console.log('🏁 messageStreamCompleted:', {
        messageId,
        hadMessage: !!message,
        wasStreaming: message?.isStreaming,
        activeStreamsBefore: state.activeStreams.length,
        allStreamingMessages: Object.keys(state.streamingMessages).length
      })
      
      if (message) {
        message.content = fullContent
        message.citations = citations
        message.isComplete = true
        message.isStreaming = false // Fix: Set streaming to false when completed
        message.status = 'complete'
        message.timestamp = timestamp instanceof Date ? timestamp.toISOString() : timestamp
        
        // Remove from active streams
        state.activeStreams = state.activeStreams.filter(id => id !== messageId)
        
        console.log('🏁 messageStreamCompleted result:', {
          messageId,
          isStreaming: message.isStreaming,
          isComplete: message.isComplete,
          status: message.status,
          activeStreamsAfter: state.activeStreams.length
        })
      }
    },

    messageStreamError: (state, action: PayloadAction<MessageErrorData>) => {
      const { messageId, error, code } = action.payload
      const message = state.streamingMessages[messageId]
      
      if (message) {
        message.hasError = true
        message.error = error
        message.isStreaming = false
        message.status = 'error'
        state.activeStreams = state.activeStreams.filter(id => id !== messageId)
      }
    },

    // Clear streaming message (when no longer needed)
    clearStreamingMessage: (state, action: PayloadAction<{ messageId: string }>) => {
      const { messageId } = action.payload
      delete state.streamingMessages[messageId]
      state.activeStreams = state.activeStreams.filter(id => id !== messageId)
    },

    // Clear all streaming messages for a session
    clearSessionMessages: (state, action: PayloadAction<{ sessionId: string }>) => {
      const { sessionId } = action.payload
      Object.keys(state.streamingMessages).forEach(messageId => {
        if (state.streamingMessages[messageId].sessionId === sessionId) {
          delete state.streamingMessages[messageId]
          state.activeStreams = state.activeStreams.filter(id => id !== messageId)
        }
      })
    },

    // Typing indicators
    userTypingStarted: (state, action: PayloadAction<{ 
      sessionId: string
      userId: string 
    }>) => {
      const { sessionId, userId } = action.payload
      
      if (!state.typing[sessionId]) {
        state.typing[sessionId] = {}
      }
      
      state.typing[sessionId][userId] = {
        isTyping: true,
        lastUpdate: new Date()
      }
    },

    userTypingStopped: (state, action: PayloadAction<{ 
      sessionId: string
      userId: string 
    }>) => {
      const { sessionId, userId } = action.payload
      
      if (state.typing[sessionId]) {
        delete state.typing[sessionId][userId]
        
        // Clean up empty session
        if (Object.keys(state.typing[sessionId]).length === 0) {
          delete state.typing[sessionId]
        }
      }
    },

    // Clear old typing indicators (cleanup)
    clearOldTypingIndicators: (state) => {
      const now = new Date()
      const timeout = 10000 // 10 seconds
      
      Object.keys(state.typing).forEach(sessionId => {
        Object.keys(state.typing[sessionId]).forEach(userId => {
          const lastUpdate = new Date(state.typing[sessionId][userId].lastUpdate)
          if (now.getTime() - lastUpdate.getTime() > timeout) {
            delete state.typing[sessionId][userId]
          }
        })
        
        // Clean up empty sessions
        if (Object.keys(state.typing[sessionId]).length === 0) {
          delete state.typing[sessionId]
        }
      })
    },

    // Reset entire socket state (on logout, etc.)
    resetSocketState: () => initialState
  }
})

export const {
  connectionStarted,
  connectionSuccess,
  connectionError,
  connectionLost,
  connectionReconnecting,
  connectionRestored,
  connectionFailed,
  connectionTerminated,
  sessionJoined,
  transferStreamingMessages,
  sessionLeft,
  messageStreamStarted,
  messageChunkReceived,
  messageCitationReceived,
  messageStatusUpdated,
  messageStreamCompleted,
  messageStreamError,
  clearStreamingMessage,
  clearSessionMessages,
  userTypingStarted,
  userTypingStopped,
  clearOldTypingIndicators,
  resetSocketState
} = socketSlice.actions

// Selectors
export const selectConnection = (state: { socket: SocketState }) => state.socket.connection
export const selectStreamingMessage = (messageId: string) => 
  (state: { socket: SocketState }) => state.socket.streamingMessages[messageId]
export const selectSessionStreamingMessages = (sessionId: string) => 
  (state: { socket: SocketState }) => 
    Object.values(state.socket.streamingMessages).filter(msg => msg.sessionId === sessionId)
export const selectActiveStreams = (state: { socket: SocketState }) => 
  Array.from(state.socket.activeStreams)
export const selectSessionTyping = (sessionId: string) => 
  (state: { socket: SocketState }) => state.socket.typing[sessionId] || {}
export const selectJoinedSessions = (state: { socket: SocketState }) => 
  Array.from(state.socket.joinedSessions)

export default socketSlice.reducer